import deepEqual from 'deep-equal';
import memoizeOne from 'memoize-one';

import { getLeapQuery } from '@atlassian/search-client';

import { ApplicationModes } from '../constants/application-modes';
import type { PrimaryProductKey } from '../constants/products';

import { getApplicationMode } from './bootstrap';
import { areLLMPoweredFeaturesEnabled } from './entitlement-checks';
import { trackFunctionPromise } from './track-promise';

/**
 * Props for fetching the Leap query.
 */
export type FetchLeapQueryProps = {
	searchQuery: string | undefined;
	primaryProduct: PrimaryProductKey | undefined;
	isRovoEnabled: boolean;
	isAdminHubAIEnabled: boolean;
	isRovoLLMEnabled: boolean;
};

/**
 * Holds the result of a Leap query fetch.
 * **/
export const getLeapQueryMemo: import('memoize-one').MemoizedFn<
	(args_0: { query: string; product: string }) => import('./track-promise').TrackedPromise<{
		title: number;
		candidates: string[];
	}>
> = memoizeOne(trackFunctionPromise(getLeapQuery), deepEqual);

/**
 * Determines if the Leap query should be fetched based on the application mode and feature flags.
 */
export const isLeapQueryEnabled: (
	appMode: ApplicationModes,
	isAdminHubAIEnabled: boolean,
	isRovoLLMEnabled: boolean,
) => boolean = (
	appMode: ApplicationModes,
	isAdminHubAIEnabled: boolean,
	isRovoLLMEnabled: boolean,
): boolean =>
	appMode === ApplicationModes.Rovo &&
	areLLMPoweredFeaturesEnabled(appMode, isAdminHubAIEnabled, isRovoLLMEnabled);

/**
 * Determines if the Leap query should be fetched based on the application mode and feature flags.
 */
export const fetchLeapQuery: (
	{
		searchQuery,
		primaryProduct,
		isRovoEnabled,
		isAdminHubAIEnabled,
		isRovoLLMEnabled,
	}: FetchLeapQueryProps,
	logError?: (error: Error) => void,
) =>
	| Promise<void | {
			title: number;
			candidates: string[];
	  }>
	| undefined = (
	{
		searchQuery,
		primaryProduct,
		isRovoEnabled,
		isAdminHubAIEnabled,
		isRovoLLMEnabled,
	}: FetchLeapQueryProps,
	logError?: (error: Error) => void,
) => {
	// load leap query if search query changes
	if (
		primaryProduct &&
		searchQuery &&
		isLeapQueryEnabled(getApplicationMode({ isRovoEnabled }), isAdminHubAIEnabled, isRovoLLMEnabled)
	) {
		// Loading state handled in the LeapQuery component
		return getLeapQueryMemo({
			query: searchQuery,
			product: primaryProduct,
		}).catch((e: Error) => {
			logError?.(e);
		});
	}
};
